/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
// import React, { useState, useMemo } from 'react';
import React, { useState } from 'react';
import { StyleSheet, css } from 'aphrodite';
import { useSelector } from 'react-redux';
import { ActionButton } from '@blend/enterprise-components';

import { State } from '../../../../redux';
import { Wrapper, Header, Table, Status } from '../../../../components/common/Pipeline';
import { notaryAPI } from '../../../../api/notary';
import { NotaryAPI } from '../../../../api/notary/types';
import { participantsColumnConfigurations } from './configs';
import { ParticipantModal, ParticipantModalFormValues } from './ParticipantModal/ParticipantModal';
import { pick, isEmpty } from '@utils/lodash';

const styles = StyleSheet.create({
  wrapper: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    maxWidth: 1400,
  },
  headerWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingRight: 40,
  },
  tableWrapper: {
    padding: '0 40px',
  },
});

export namespace ParticipantList {
  export interface Props {
    roomId: string;
    isLoading: boolean;
  }
}

export const createParticipantRows = (allParticipants: NotaryAPI.Participant[]) => {
  // filter for all secondary participants in the list of participants
  const allSecondary = allParticipants.filter(participant => !!participant.primaryParticipantId);

  return allParticipants
    .filter(participant => !participant.primaryParticipantId) // filtering for primary participants
    .map(primary => {
      // find all the secondaries for a primary participant
      const secondaryParticipants = allSecondary.filter(secondary => secondary.primaryParticipantId === primary.id);
      // create an array of all titles associated with the primary for display
      const titles = [primary.title].concat(secondaryParticipants.map(p => p.title));
      const rolesAndSignatures = secondaryParticipants.map(secondary => ({
        ...(secondary.id && { id: secondary.id }), // only add an id key if it exists
        role: secondary.title,
        signature: secondary.signature,
      }));

      const primaryParticipant = { ...primary, allTitles: titles.join(', ') };
      // add a rolesAndSignatures key in primary participant object only when there are multiple roles
      return rolesAndSignatures.length ? { ...primaryParticipant, rolesAndSignatures } : primaryParticipant;
    });
};

export const ParticipantList: React.FC<ParticipantList.Props> = ({ roomId, isLoading }) => {
  const { roomDetail } = useSelector((state: State) => state);
  const [addParticipantModalIsOpen, setAddParticipantModalIsOpen] = useState(false);

  const participants = roomDetail.participants as NotaryAPI.Participant[];
  const uniqueParticipants = createParticipantRows(participants);

  const canAddParticipant = !roomDetail.room!.isCallInProgress;

  const handleAddParticipantSubmit = async (formValues: ParticipantModalFormValues) => {
    const newParticipant = pick(formValues, [
      'title',
      'firstName',
      'middleName',
      'lastName',
      'suffix',
      'email',
      'phoneNumber',
      'requiresKba',
      'signature',
      'rolesAndSignatures',
    ]);

    const { firstName, middleName, lastName, suffix, requiresKba, title } = newParticipant;
    if (!firstName || !lastName || !title || requiresKba === undefined) {
      throw new Error('Missing field when adding participant');
    }
    await notaryAPI.rooms.createParticipant(roomId, {
      ...newParticipant,
      firstName,
      middleName,
      lastName,
      suffix,
      requiresKba,
      title,
    });
  };

  return (
    <div className={css(styles.wrapper)}>
      <Wrapper>
        <div className={css(styles.headerWrapper)}>
          <Header header="Participants" />
          {canAddParticipant && (
            <ActionButton
              data-dd-privacy="allow"
              data-it="participantList-addNewParticipantBtn"
              btnSize="md"
              label="Add new participant"
              onClick={() => setAddParticipantModalIsOpen(true)}
              type="button"
            />
          )}
        </div>
        {!isLoading && (
          <div className={css(styles.tableWrapper)}>
            <Table data={uniqueParticipants} tableColumns={participantsColumnConfigurations()} />
          </div>
        )}
        <Status
          isLoading={isLoading}
          isEmpty={isEmpty(roomDetail.participants)}
          emptyMessage={'No participants here at the moment.'}
        />
      </Wrapper>
      <ParticipantModal
        headerText="Add New Participant"
        subHeaderText={
          'Enter the details of a participant you want to be added to this notarization. They will be emailed a Blend Signing Room link for this notarization appointment.'
        }
        roomId={roomId}
        isOpen={addParticipantModalIsOpen}
        handleFormSubmit={handleAddParticipantSubmit}
        handleClose={() => setAddParticipantModalIsOpen(false)}
        isGenericParticipant={true}
      />
    </div>
  );
};
